import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Button, Card, Col, Divider, Form, Input, InputNumber, Modal,
  Popconfirm, Row, Select, Space, Table, Tabs, Tag, Typography, Upload, message
} from "antd";
import {
  BookOutlined, DeleteOutlined, EditOutlined, GlobalOutlined, ImportOutlined,
  PlusOutlined, ReloadOutlined, TagsOutlined, UploadOutlined
} from "@ant-design/icons";

import {
  appContentEnsure, createAppContent, deleteAppContent, getAppContents, getAppGenres,
  getAppMarkets, getLegacyBooks, saveAppGenre, saveAppMarket, updateAppContent,
  uploadAppCover
} from "../../../api/appContent";

const { Title, Text } = Typography;
const { TextArea } = Input;

const VN_BOOK_SECTIONS = [
  { value: "book_translated", label: "Sách dịch" },
  { value: "book_top_free", label: "Top miễn phí" },
  { value: "book_medical", label: "Sách Y khoa" },
  { value: "book_other", label: "Sách khác" },
  { value: "story_new", label: "Truyện mới cập nhật" }
];

const STORY_SECTIONS = [
  { value: "story_new", label: "Mới cập nhật" },
  { value: "story_popular", label: "Đọc nhiều" },
  { value: "story_recommended", label: "Đề xuất" },
  { value: "story_free", label: "Miễn phí" },
  { value: "story_ongoing", label: "Đang ra" },
  { value: "story_completed", label: "Đã hoàn thành" }
];

const GLOBAL_SECTIONS = [
  { value: "story_new", label: "New Updates" },
  { value: "story_popular", label: "Popular" },
  { value: "story_recommended", label: "Recommended" },
  { value: "story_free", label: "Free Stories / Books" },
  { value: "story_ongoing", label: "Ongoing" },
  { value: "story_completed", label: "Completed" },
  { value: "global_books", label: "Books" },
  { value: "global_novels", label: "Novels" },
  { value: "global_comics", label: "Comics" }
];

const initialValues = {
  content_type: "book",
  primary_category: "translated",
  locale: "vi-VN",
  source_kind: "drive_folder",
  access_type: "free",
  publish_status: "draft",
  release_status: "ongoing",
  markets: ["VN"],
  section_keys: [],
  genre_ids: [],
  sort_order: 0,
  commerce_price: 0,
  commerce_discount: 0
};

export default function AppContentManage() {
  const [form] = Form.useForm();
  const [marketForm] = Form.useForm();
  const [genreForm] = Form.useForm();

  const [rows, setRows] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [genres, setGenres] = useState([]);
  const [legacy, setLegacy] = useState([]);

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [marketOpen, setMarketOpen] = useState(false);
  const [genreOpen, setGenreOpen] = useState(false);
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedLegacy, setSelectedLegacy] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  const contentType = Form.useWatch("content_type", form) || "book";
  const selectedMarkets = Form.useWatch("markets", form) || ["VN"];

  const loadAll = async () => {
    setLoading(true);
    try {
      await appContentEnsure();
      const [a,b,c] = await Promise.all([
        getAppContents(),
        getAppMarkets(),
        getAppGenres("")
      ]);
      setRows(Array.isArray(a?.data?.data) ? a.data.data : []);
      setMarkets(Array.isArray(b?.data?.data) ? b.data.data : []);
      setGenres(Array.isArray(c?.data?.data) ? c.data.data : []);
    } catch (e) {
      message.error(e?.response?.data?.message || "Không tải được OS BOOKS Admin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const sectionOptions = useMemo(() => {
    const hasGlobal = selectedMarkets.some(x => x !== "VN");
    if (contentType === "book") {
      return hasGlobal
        ? [...VN_BOOK_SECTIONS.filter(x => x.value !== "book_medical"), ...GLOBAL_SECTIONS]
        : VN_BOOK_SECTIONS;
    }
    return hasGlobal ? GLOBAL_SECTIONS : STORY_SECTIONS;
  }, [contentType, selectedMarkets]);

  const genreOptions = useMemo(() => {
    const mk = selectedMarkets?.[0] || "VN";
    return genres
      .filter(x => x.market_code === mk)
      .map(x => ({ value: x.genre_id, label: x.genre_name }));
  }, [genres, selectedMarkets]);

  const uploadCover = async ({ file, onSuccess, onError }) => {
    try {
      setUploadingCover(true);

      const formData = new FormData();
      formData.append("product_link", file);

      const res = await uploadAppCover(formData);
      const raw = res?.data;

      let path = "";

      if (typeof raw === "string") {
        path = raw;
      } else {
        path =
          raw?.url ||
          raw?.path ||
          raw?.link ||
          raw?.data?.url ||
          raw?.data?.path ||
          raw?.data?.link ||
          "";
      }

      if (!path) {
        throw new Error("Server không trả đường dẫn ảnh");
      }

      const url =
        path.startsWith("http://") || path.startsWith("https://")
          ? path
          : `https://api.cuahangykhoa.com${path.startsWith("/") ? "" : "/"}${path}`;

      form.setFieldsValue({
        cover_url: url
      });

      // Nếu đang sửa nội dung có sẵn thì lưu cover_url ngay.
      if (editing?.content_id) {
        const values = form.getFieldsValue(true);

        await updateAppContent(
          editing.content_id,
          buildPayload({
            ...values,
            cover_url: url
          })
        );

        message.success("Đã tải và lưu ảnh bìa");
      } else {
        message.success("Đã tải ảnh bìa. Bấm Lưu để tạo nội dung.");
      }

      if (onSuccess) onSuccess(raw);

    } catch (e) {
      console.error("UPLOAD COVER ERROR:", e);

      message.error(
        e?.response?.data?.message ||
        e?.message ||
        "Không tải được ảnh bìa"
      );

      if (onError) onError(e);

    } finally {
      setUploadingCover(false);
    }
  };

  const removeCover = () => {
    form.setFieldsValue({
      cover_url: ""
    });
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue(initialValues);
    setOpen(true);
  };

  const openEdit = r => {
    setEditing(r);
    form.setFieldsValue({
      ...initialValues,
      ...r,
      locale: r.default_locale || "vi-VN",
      markets: String(r.markets || "").split(",").filter(Boolean),
      section_keys: String(r.sections || "").split(",").filter(Boolean).map(x => x.split(":").slice(1).join(":")),
      genre_ids: String(r.genre_ids || "").split(",").filter(Boolean).map(Number),
      commerce_price: Number(r.commerce_price || 0),
      commerce_discount: Number(r.commerce_discount || 0)
    });
    setOpen(true);
  };

  const buildPayload = values => {
    const marketsValue = values.markets || [];
    const sections = [];
    (values.section_keys || []).forEach(section_key => {
      marketsValue.forEach(market_code => {
        if (market_code !== "VN" && section_key === "book_medical") return;
        sections.push({
          market_code,
          section_key,
          sort_order: Number(values.sort_order || 0)
        });
      });
    });
    return {
      ...values,
      default_locale: values.locale,
      sections
    };
  };

  const saveContent = async () => {
    try {
      const values = await form.validateFields();
      if ((values.markets || []).some(x => x !== "VN") && (values.section_keys || []).includes("book_medical")) {
        return message.error("Sách Y khoa chỉ dành cho Việt Nam.");
      }

      const payload = buildPayload(values);
      if (editing?.content_id) {
        await updateAppContent(editing.content_id, payload);
        message.success("Đã cập nhật");
      } else {
        await createAppContent(payload);
        message.success("Đã thêm nội dung");
      }
      setOpen(false);
      loadAll();
    } catch (e) {
      if (!e?.errorFields) message.error(e?.response?.data?.message || e?.message || "Không lưu được");
    }
  };

  const openLegacy = async () => {
    setLegacyOpen(true);
    setSelectedLegacy(null);
    try {
      const res = await getLegacyBooks();
      setLegacy(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (e) {
      message.error(e?.response?.data?.message || "Không tải được sản phẩm cũ");
    }
  };

  const importLegacyToForm = r => {
    setSelectedLegacy(r);
    setLegacyOpen(false);
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      ...initialValues,
      product_id: r.product_id,
      title: r.product_name || "",
      slug: r.slug || "",
      cover_url: r.image || "",
      source_url: r.sach_link || r.drive_link || "",
      content_type: "book",
      primary_category: "medical",
      markets: ["VN"],
      section_keys: ["book_medical"]
    });
    setOpen(true);
  };

  const columns = [
    {
      title: "Nội dung",
      render: (_,r) => (
        <Space>
          {r.cover_url ? (
            <img src={r.cover_url} alt="" style={{width:42,height:58,objectFit:"cover",borderRadius:6}} />
          ) : (
            <div style={{width:42,height:58,display:"grid",placeItems:"center",background:"#f5f5f5",borderRadius:6}}>
              <BookOutlined />
            </div>
          )}
          <div>
            <Text strong>{r.title || r.slug}</Text><br/>
            <Text type="secondary" style={{fontSize:12}}>
              {r.product_id ? `Liên kết SP #${r.product_id}` : "Nội dung riêng của app"}
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: "Loại",
      dataIndex: "content_type",
      render: x => <Tag>{x === "book" ? "Sách" : x === "novel" ? "Truyện chữ" : "Truyện tranh"}</Tag>
    },
    {
      title: "Danh mục",
      dataIndex: "primary_category",
      render: x => <Tag color="blue">{x || "—"}</Tag>
    },
    {
      title: "Quốc gia",
      dataIndex: "markets",
      render: x => String(x||"").split(",").filter(Boolean).map(v => <Tag key={v}>{v}</Tag>)
    },
    {
      title: "Trạng thái",
      dataIndex: "publish_status",
      render: x => <Tag color={x==="published"?"green":x==="hidden"?"red":"gold"}>{x}</Tag>
    },
    {
      title: "",
      render: (_,r) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Xóa nội dung này?" onConfirm={async () => {
            await deleteAppContent(r.content_id);
            message.success("Đã xóa");
            loadAll();
          }}>
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const legacyColumns = [
    { title: "ID", dataIndex: "product_id", width: 80 },
    { title: "Tên sản phẩm", dataIndex: "product_name" },
    { title: "Định dạng", dataIndex: "sach_format", width: 130 },
    {
      title: "",
      width: 100,
      render: (_,r) => <Button type="primary" onClick={() => importLegacyToForm(r)}>Chọn</Button>
    }
  ];

  return (
    <div style={{padding:20}}>
      <Space style={{width:"100%",justifyContent:"space-between",marginBottom:18}} wrap>
        <div>
          <Title level={2} style={{margin:0}}>OS BOOKS</Title>
          <Text type="secondary">Quản lý sách, truyện, danh mục và quốc gia.</Text>
        </div>
        <Space wrap>
          <Button icon={<ImportOutlined />} onClick={openLegacy}>Nhập từ admin cũ</Button>
          <Button icon={<TagsOutlined />} onClick={() => setGenreOpen(true)}>Thể loại</Button>
          <Button icon={<GlobalOutlined />} onClick={() => setMarketOpen(true)}>Quốc gia</Button>
          <Button icon={<ReloadOutlined />} onClick={loadAll}>Làm mới</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Thêm nội dung</Button>
        </Space>
      </Space>

      <Alert
        showIcon
        type="info"
        style={{marginBottom:18}}
        message="Cấu trúc hiện tại đã khớp với app"
        description="Việt Nam: Sách dịch · Top miễn phí · Sách Y khoa · Sách khác · Truyện mới cập nhật. Truyện: Mới cập nhật · Đọc nhiều · Đề xuất · Miễn phí · Đang ra · Đã hoàn thành. Global/English không có Sách Y khoa."
      />

      <Card>
        <Table rowKey="content_id" columns={columns} dataSource={rows} loading={loading} scroll={{x:900}} />
      </Card>

      <Modal
        title={editing ? "Sửa nội dung" : selectedLegacy ? "Nhập sách từ admin cũ" : "Thêm nội dung"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={saveContent}
        width={900}
        okText="Lưu"
      >
        <Form form={form} layout="vertical" initialValues={initialValues}>
          <Form.Item name="product_id" hidden><Input /></Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="content_type" label="Loại nội dung">
                <Select options={[
                  {value:"book",label:"Sách"},
                  {value:"novel",label:"Truyện chữ"},
                  {value:"comic",label:"Truyện tranh"}
                ]}/>
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item name="locale" label="Ngôn ngữ">
                <Select options={[
                  {value:"vi-VN",label:"Tiếng Việt"},
                  {value:"en",label:"English"},
                  {value:"ja-JP",label:"日本語"},
                  {value:"ko-KR",label:"한국어"},
                  {value:"id-ID",label:"Bahasa Indonesia"}
                ]}/>
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item name="markets" label="Quốc gia / thị trường">
                <Select mode="multiple" options={markets.map(x => ({
                  value:x.market_code,
                  label:`${x.market_name} (${x.market_code})`
                }))}/>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="title" label="Tên sách / truyện" rules={[{required:true}]}>
            <Input/>
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}><Form.Item name="author" label="Tác giả"><Input/></Form.Item></Col>
            <Col xs={24} md={12}><Form.Item name="slug" label="Slug"><Input/></Form.Item></Col>
          </Row>

          <Form.Item
            noStyle
            shouldUpdate={(prev, current) =>
              prev.cover_url !== current.cover_url
            }
          >
            {() => {
              const coverUrl = form.getFieldValue("cover_url") || "";

              return (
                <Form.Item label="Ảnh bìa">
                  <Space align="start" wrap>
                    <div
                      style={{
                        width: 110,
                        minHeight: 150,
                        border: "1px solid #d9d9d9",
                        borderRadius: 10,
                        overflow: "hidden",
                        background: "#fafafa",
                        display: "grid",
                        placeItems: "center"
                      }}
                    >
                      {coverUrl ? (
                        <img
                          src={coverUrl}
                          alt="Ảnh bìa"
                          style={{
                            width: "100%",
                            height: 150,
                            objectFit: "cover"
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            textAlign: "center",
                            color: "#999",
                            padding: 12
                          }}
                        >
                          <BookOutlined
                            style={{
                              fontSize: 28,
                              marginBottom: 8
                            }}
                          />
                          <div>Chưa có ảnh</div>
                        </div>
                      )}
                    </div>

                    <Space direction="vertical">
                      <Upload
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        maxCount={1}
                        showUploadList={false}
                        customRequest={uploadCover}
                        beforeUpload={file => {
                          const isImage =
                            file.type &&
                            file.type.startsWith("image/");

                          if (!isImage) {
                            message.error(
                              "Chỉ được tải file ảnh"
                            );
                            return Upload.LIST_IGNORE;
                          }

                          const isLt10M =
                            file.size / 1024 / 1024 < 10;

                          if (!isLt10M) {
                            message.error(
                              "Ảnh phải nhỏ hơn 10MB"
                            );
                            return Upload.LIST_IGNORE;
                          }

                          return true;
                        }}
                      >
                        <Button
                          type="primary"
                          icon={<UploadOutlined />}
                          loading={uploadingCover}
                        >
                          {coverUrl
                            ? "Đổi ảnh bìa"
                            : "Tải ảnh bìa"}
                        </Button>
                      </Upload>

                      {coverUrl && (
                        <Button
                          danger
                          onClick={removeCover}
                        >
                          Xóa ảnh
                        </Button>
                      )}

                      <Text
                        type="secondary"
                        style={{
                          fontSize: 12,
                          maxWidth: 340
                        }}
                      >
                        JPG, PNG, WebP hoặc GIF. Tối đa 10MB.
                        Ảnh sẽ được tải trực tiếp lên server.
                      </Text>
                    </Space>
                  </Space>

                  <Form.Item
                    name="cover_url"
                    hidden
                  >
                    <Input />
                  </Form.Item>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Divider orientation="left">Danh mục trong app</Divider>

          <Row gutter={16}>
            <Col xs={24} md={10}>
              <Form.Item name="primary_category" label="Danh mục chính">
                <Select options={
                  contentType === "book"
                    ? [
                        {value:"translated",label:"Sách dịch"},
                        {value:"free",label:"Sách miễn phí"},
                        {value:"medical",label:"Sách Y khoa"},
                        {value:"other",label:"Sách khác"},
                        {value:"global_book",label:"Books (Global)"}
                      ]
                    : [
                        {value:"story",label:"Truyện"},
                        {value:"novel",label:"Truyện chữ"},
                        {value:"comic",label:"Truyện tranh"}
                      ]
                }/>
              </Form.Item>
            </Col>

            <Col xs={24} md={14}>
              <Form.Item name="section_keys" label="Hiển thị ở các mục">
                <Select mode="multiple" options={sectionOptions}/>
              </Form.Item>
            </Col>
          </Row>

          {contentType !== "book" && (
            <Form.Item name="genre_ids" label="Thể loại truyện">
              <Select mode="multiple" options={genreOptions} placeholder="Ngôn tình, Trinh thám, Fantasy..."/>
            </Form.Item>
          )}

          <Divider orientation="left">File / Google Drive</Divider>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="source_kind" label="Nguồn">
                <Select options={[
                  {value:"drive_folder",label:"Thư mục Google Drive"},
                  {value:"drive_file",label:"File Google Drive"},
                  {value:"external",label:"Link khác"}
                ]}/>
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item name="source_url" label="Link file / thư mục"><Input/></Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item name="access_type" label="Quyền truy cập">
                <Select options={[
                  {value:"free",label:"Miễn phí"},
                  {value:"paid",label:"Trả phí"}
                ]}/>
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="commerce_price" label="Giá gốc (đ)">
                <InputNumber min={0} step={1000} style={{width:"100%"}} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="commerce_discount" label="Giảm giá (%)">
                <InputNumber min={0} max={100} style={{width:"100%"}} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="release_status" label="Tình trạng">
                <Select options={[
                  {value:"ongoing",label:"Đang ra"},
                  {value:"completed",label:"Đã hoàn thành"}
                ]}/>
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="publish_status" label="Xuất bản">
                <Select options={[
                  {value:"draft",label:"Bản nháp"},
                  {value:"published",label:"Đang hiển thị"},
                  {value:"hidden",label:"Ẩn"}
                ]}/>
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="sort_order" label="Thứ tự"><InputNumber min={0} style={{width:"100%"}}/></Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Mô tả"><TextArea rows={3}/></Form.Item>

          {selectedMarkets.some(x => x !== "VN") && (
            <Alert
              type="warning"
              showIcon
              message="Global/English không hiển thị Sách Y khoa"
              description="Hệ thống sẽ không gán mục Sách Y khoa cho bất kỳ thị trường nào ngoài Việt Nam."
            />
          )}
        </Form>
      </Modal>

      <Modal title="Nhập từ admin cũ" open={legacyOpen} onCancel={() => setLegacyOpen(false)} footer={null} width={900}>
        <Alert
          type="info"
          showIcon
          style={{marginBottom:12}}
          message="Chọn một sản phẩm cũ"
          description="Sau khi chọn, hệ thống chỉ liên kết bằng product_id. Sản phẩm bán hàng cũ vẫn giữ nguyên."
        />
        <Table rowKey="product_id" columns={legacyColumns} dataSource={legacy} pagination={{pageSize:10}} />
      </Modal>

      <Modal title="Thêm quốc gia" open={marketOpen} onCancel={() => setMarketOpen(false)} onOk={async () => {
        const v = await marketForm.validateFields();
        await saveAppMarket(v);
        message.success("Đã lưu quốc gia");
        setMarketOpen(false);
        marketForm.resetFields();
        loadAll();
      }}>
        <Form form={marketForm} layout="vertical">
          <Form.Item name="market_code" label="Mã quốc gia" rules={[{required:true}]}>
            <Input placeholder="US / JP / ID / BR"/>
          </Form.Item>
          <Form.Item name="market_name" label="Tên" rules={[{required:true}]}>
            <Input placeholder="United States / Japan / Indonesia"/>
          </Form.Item>
          <Form.Item name="default_locale" label="Ngôn ngữ mặc định" initialValue="en">
            <Input placeholder="en / ja-JP / id-ID"/>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Thêm thể loại truyện" open={genreOpen} onCancel={() => setGenreOpen(false)} onOk={async () => {
        const v = await genreForm.validateFields();
        await saveAppGenre(v);
        message.success("Đã lưu thể loại");
        setGenreOpen(false);
        genreForm.resetFields();
        loadAll();
      }}>
        <Form form={genreForm} layout="vertical">
          <Form.Item name="market_code" label="Thị trường" initialValue="VN">
            <Select options={markets.map(x => ({value:x.market_code,label:x.market_name}))}/>
          </Form.Item>
          <Form.Item name="locale" label="Ngôn ngữ" initialValue="vi-VN"><Input/></Form.Item>
          <Form.Item name="genre_name" label="Tên thể loại" rules={[{required:true}]}>
            <Input placeholder="Ngôn tình / Fantasy / Mystery..."/>
          </Form.Item>
          <Form.Item name="sort_order" label="Thứ tự" initialValue={0}><InputNumber min={0}/></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
